import React, { useEffect, useMemo, useState } from "react";
import { getToken } from "./tokenStorage";
import { fetchJsonCached, peekCachedJson, storeCachedJson } from "./apiCache";
import {
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

const WEEKDAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function fmtDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("ru-RU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Clock() {
  const [time, setTime] = React.useState(new Date());
  const hourHandRef = React.useRef(null);
  const minHandRef = React.useRef(null);
  const secHandRef = React.useRef(null);
  const rafRef = React.useRef(null);

  React.useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const animate = () => {
      const now = new Date();
      const ms = now.getMilliseconds();
      const s = now.getSeconds() + ms / 1000;
      const m = now.getMinutes() + s / 60;
      const h = now.getHours() + m / 60;
      const secAngle = s * 6;
      const minAngle = m * 6;
      const hourAngle = (h % 12) * 30;
      if (secHandRef.current) secHandRef.current.style.transform = `translate(-50%, -100%) rotate(${secAngle}deg)`;
      if (minHandRef.current) minHandRef.current.style.transform = `translate(-50%, -100%) rotate(${minAngle}deg)`;
      if (hourHandRef.current) hourHandRef.current.style.transform = `translate(-50%, -100%) rotate(${hourAngle}deg)`;
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const weekday = time.toLocaleDateString("ru-RU", { weekday: "long" });
  const dateLabel = time.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="flex justify-center my-8">
      <div className="group relative w-72 h-72 sm:w-80 sm:h-80 rounded-full border-[10px] border-slate-200 dark:border-slate-700/60 bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-[#222428] dark:via-[#26282e] dark:to-[#1e2026] shadow-[0_14px_34px_rgba(15,23,42,0.18)] dark:shadow-[0_14px_34px_rgba(0,0,0,0.5)] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 rounded-full ring-1 ring-white/70" />

        <div className="absolute top-[23%] text-center z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          <div className="text-xs sm:text-sm font-semibold text-slate-800">{dateLabel}</div>
          <div className="text-[10px] sm:text-xs tracking-[0.08em] text-slate-600 font-medium mt-1 text-center">
            {weekday}
          </div>
        </div>

        <div className="absolute top-[74%] -translate-y-1/2 text-sm sm:text-base font-mono font-bold text-slate-900 dark:text-slate-100 bg-white/95 dark:bg-[rgba(38,40,46,0.95)] px-3.5 py-1.5 rounded-full shadow-md border border-slate-300 dark:border-slate-600/60 select-none z-30">
          {time.toLocaleTimeString("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </div>

        <div
          ref={hourHandRef}
          className="absolute z-10 bg-slate-800 dark:bg-slate-200 origin-bottom rounded-full opacity-80"
          style={{
            width: "6px",
            height: "74px",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -100%) rotate(0deg)",
          }}
        />

        <div
          ref={minHandRef}
          className="absolute z-10 bg-slate-600 dark:bg-slate-300 origin-bottom rounded-full opacity-85"
          style={{
            width: "4px",
            height: "104px",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -100%) rotate(0deg)",
          }}
        />

        <div
          ref={secHandRef}
          className="absolute z-10 bg-rose-500 origin-bottom opacity-90 rounded-full"
          style={{
            width: "2px",
            height: "122px",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -100%) rotate(0deg)",
          }}
        />

        <div className="absolute z-20 w-4 h-4 bg-slate-900 dark:bg-slate-100 rounded-full ring-[4px] ring-white/90 dark:ring-slate-900/90" />

        {[...Array(60)].map((_, i) => {
          const major = i % 5 === 0;
          return (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                background: major ? "#334155" : "#94a3b8",
                width: major ? 3 : 1.5,
                height: major ? 18 : 7,
                left: "50%",
                top: "50%",
                transform: `translate(-50%, -50%) rotate(${i * 6 + 90}deg) translateY(-134px)`,
                transformOrigin: "center",
              }}
            />
          );
        })}

        {[
          { n: "12", deg: 0 },
          { n: "3", deg: 90 },
          { n: "6", deg: 180 },
          { n: "9", deg: 270 },
        ].map(({ n, deg }) => (
          <div
            key={n}
            className="absolute select-none text-slate-700 font-semibold text-sm sm:text-base"
            style={{
              left: "50%",
              top: "50%",
              transform: `translate(-50%, -50%) rotate(${deg}deg) translateY(-108px) rotate(${-deg}deg)`,
            }}
          >
            {n}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const API = window.location.origin;
  const [uiReady, setUiReady] = useState(false);
  const [roomsCardFlipped, setRoomsCardFlipped] = useState(false);
  const [roomsKickflipActive, setRoomsKickflipActive] = useState(false);
  const [roomsKickflipDir, setRoomsKickflipDir] = useState(1);
  const [roomsByBuilding, setRoomsByBuilding] = useState({
    "1": { busy: 0, free: 0, total: 0, busyPercent: 0 },
    "2": { busy: 0, free: 0, total: 0, busyPercent: 0 },
  });

  const [metrics, setMetrics] = useState({
    lessonsToday: 0,
    busyNow: 0,
    freeNow: 0,
    pairState: "none",
    activePairIndex: null,
    rangeId: null,
    lastReparseSuccess: null,
    changesAddedCount: 0,
    changesRemovedCount: 0,
    weekLoad: [],
  });
  const [recent, setRecent] = useState([]);


  useEffect(() => {
    const mapRoomsMetrics = (data) => {
      const busy = data.auditories_busy_now || 0;
      const free = data.auditories_free_now || 0;
      const total = busy + free;
      const busyPercent = total > 0 ? Math.round((busy / total) * 100) : 0;
      return { busy, free, total, busyPercent };
    };

    const fetchMetrics = async () => {
      try {
        const token = getToken();
        if (!token) return;

        const cached = peekCachedJson("dashboard:metrics", 30_000);
        if (cached) {
          setRoomsByBuilding(cached.roomsByBuilding);
          setMetrics(cached.metrics);
          setRecent(cached.recent);
          return;
        }

        const [dataBuilding1, dataBuilding2] = await Promise.all([
          fetchJsonCached(`${API}/dashboard/metrics?building=1`, {
            cacheKey: "dashboard:metrics:building:1",
            ttlMs: 30_000,
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetchJsonCached(`${API}/dashboard/metrics?building=2`, {
            cacheKey: "dashboard:metrics:building:2",
            ttlMs: 30_000,
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({})),
        ]);

        const roomsByBuilding = {
          "1": mapRoomsMetrics(dataBuilding1),
          "2": mapRoomsMetrics(dataBuilding2),
        };

        const nextMetrics = {
          lessonsToday: dataBuilding1.lessons_today || 0,
          busyNow: dataBuilding1.auditories_busy_now || 0,
          freeNow: dataBuilding1.auditories_free_now || 0,
          pairState: dataBuilding1.pair_state || "none",
          activePairIndex: dataBuilding1.active_pair_index,
          rangeId: dataBuilding1.range_id || null,
          lastReparseSuccess: dataBuilding1.last_reparse_success || null,
          changesAddedCount: dataBuilding1.changes_added_count || 0,
          changesRemovedCount: dataBuilding1.changes_removed_count || 0,
          weekLoad: Array.isArray(dataBuilding1.week_load) ? dataBuilding1.week_load : [],
        };
        const nextRecent = Array.isArray(dataBuilding1.recent) ? dataBuilding1.recent.slice(0, 3) : [];

        setRoomsByBuilding(roomsByBuilding);
        setMetrics(nextMetrics);
        setRecent(nextRecent);
        storeCachedJson("dashboard:metrics", {
          roomsByBuilding,
          metrics: nextMetrics,
          recent: nextRecent,
        });
      } catch (err) {
        console.error("Failed to load dashboard metrics:", err);
      }
    };

    fetchMetrics();
  }, [API]);

  useEffect(() => {
    const t = setTimeout(() => setUiReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!roomsKickflipActive) return;
    const t = setTimeout(() => setRoomsKickflipActive(false), 720);
    return () => clearTimeout(t);
  }, [roomsKickflipActive]);

  const handleRoomsCardClick = () => {
    setRoomsKickflipDir(roomsCardFlipped ? -1 : 1);
    setRoomsKickflipActive(false);
    requestAnimationFrame(() => setRoomsKickflipActive(true));
    setRoomsCardFlipped((prev) => !prev);
  };

  const weekLoadData = useMemo(
    () =>
      (metrics.weekLoad || []).map((item) => ({
        day: WEEKDAY_LABELS[item.weekday] || String(item.weekday),
        lessons: item.count || 0,
      })),
    [metrics.weekLoad]
  );

  const weekRangeLabel = useMemo(() => {
    const raw = (metrics.rangeId || "").trim();
    if (!raw || !raw.includes("_")) return "Диапазон недели не указан";
    const [from, to] = raw.split("_", 2);
    if (!from || !to) return "Диапазон недели не указан";
    return `с ${from} по ${to}`;
  }, [metrics.rangeId]);

  const pairStateLabel = useMemo(() => {
    if (metrics.pairState === "pair") return `Идет ${metrics.activePairIndex || "?"} пара`;
    if (metrics.pairState === "break") return `Перерыв, далее ${metrics.activePairIndex || "?"} пара`;
    if (metrics.pairState === "before") return `До начала, ближайшая ${metrics.activePairIndex || "?"} пара`;
    if (metrics.pairState === "after") return "Пары на сегодня завершены";
    return "Состояние пар недоступно";
  }, [metrics.pairState, metrics.activePairIndex]);


  const StatusIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v5l3 2" />
    </svg>
  );

  const RoomsIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="14" rx="2.2" />
      <path d="M9 9h6M9 12h6M9 15h4" />
    </svg>
  );

  const SyncIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 7v5h-5" />
      <path d="M4 17v-5h5" />
      <path d="M7.5 7.5A7 7 0 0 1 20 12" />
      <path d="M16.5 16.5A7 7 0 0 1 4 12" />
    </svg>
  );

  const statCardSymbolCloud = [
    { top: "8%", left: "8%", size: "18px", opacity: 0.22 },
    { top: "12%", left: "30%", size: "20px", opacity: 0.24 },
    { top: "10%", left: "56%", size: "19px", opacity: 0.21 },
    { top: "14%", left: "78%", size: "18px", opacity: 0.23 },
    { top: "34%", left: "12%", size: "21px", opacity: 0.24 },
    { top: "38%", left: "40%", size: "19px", opacity: 0.22 },
    { top: "36%", left: "70%", size: "20px", opacity: 0.23 },
    { top: "60%", left: "16%", size: "19px", opacity: 0.21 },
    { top: "62%", left: "48%", size: "21px", opacity: 0.24 },
    { top: "74%", left: "68%", size: "20px", opacity: 0.23 },
    { top: "22%", left: "18%", size: "18px", opacity: 0.22 },
    { top: "70%", left: "36%", size: "19px", opacity: 0.23 },
  ];

  const StatCard = ({ title, value, note, icon, glyph, glyphColor = "#64748b", accent = "from-sky-500 to-blue-500", delay = 0 }) => (
    <div
      className={`group relative overflow-hidden rounded-2xl p-4 sm:p-6 text-center border border-slate-200/80 dark:border-slate-700/40 bg-gradient-to-b from-white to-slate-50 dark:from-[#2a2c32] dark:to-[#222428] shadow-[0_8px_24px_rgba(15,23,42,0.08)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.35)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_34px_rgba(15,23,42,0.14)] dark:hover:shadow-[0_16px_34px_rgba(0,0,0,0.5)] ${
        uiReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${accent}`} />
      {glyph ? (
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {statCardSymbolCloud.map((item, i) => (
            <span
              key={`stat-glyph-${i}`}
              className="absolute select-none"
              style={{
                top: item.top,
                left: item.left,
                fontSize: item.size,
                opacity: item.opacity,
                color: glyphColor,
                transform: "rotate(-12deg)",
                lineHeight: 1,
                textShadow: "0 0 8px rgba(255,255,255,0.18)",
              }}
            >
              {glyph}
            </span>
          ))}
        </div>
      ) : null}
      <div className="relative z-10 mx-auto mb-2 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300 group-hover:scale-105 transition-transform">
        {icon}
      </div>
      <div className="relative z-10 text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 leading-tight">{value}</div>
      <div className="relative z-10 mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-400">{title}</div>
      {note ? <div className="relative z-10 mt-2 text-[11px] sm:text-xs text-slate-500 dark:text-slate-500">{note}</div> : null}
    </div>
  );

  return (
    <>
      <div className="-mt-4 sm:-mt-6 relative isolate overflow-hidden rounded-3xl p-3 sm:p-5">
        <div className="relative z-10">
          <Clock />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
            <StatCard
              title="Статус сейчас"
              value={pairStateLabel}
              note={`Занятий сегодня: ${metrics.lessonsToday}`}
              icon={<StatusIcon />}
              accent="from-indigo-500 to-sky-500"
              glyph="⏱"
              glyphColor="#0ea5e9"
              delay={40}
            />
            <div
              className={`relative [perspective:1200px] transition-all duration-500 ${
                uiReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
              }`}
              style={{ transitionDelay: "120ms" }}
            >
              <button
                type="button"
                onClick={handleRoomsCardClick}
                className="w-full h-full min-h-[170px] rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70"
                aria-label="Переключить корпус на карточке аудиторий"
              >
                <div
                  className={`relative h-full min-h-[170px] rounded-2xl transition-transform duration-300 [transform-style:preserve-3d] ${
                    roomsKickflipActive
                      ? (roomsKickflipDir > 0 ? "kickflip-flip-forward" : "kickflip-flip-back")
                      : ""
                  }`}
                  style={{ transform: roomsCardFlipped ? "rotateX(180deg)" : "rotateX(0deg)" }}
                >
                  <div className="absolute inset-0 [backface-visibility:hidden]">
                    <StatCard
                      title="Аудитории сейчас - корпус 1"
                      value={`${roomsByBuilding["1"].busy}/${roomsByBuilding["1"].total || 0} занято`}
                      note={`${roomsByBuilding["1"].busyPercent}% загрузка - нажмите для корпуса 2`}
                      icon={<RoomsIcon />}
                      accent="from-emerald-500 to-teal-500"
                      glyph="⌂"
                      glyphColor="#2563eb"
                    />
                  </div>
                  <div className="absolute inset-0 [transform:rotateX(180deg)] [backface-visibility:hidden]">
                    <StatCard
                      title="Аудитории сейчас - корпус 2"
                      value={`${roomsByBuilding["2"].busy}/${roomsByBuilding["2"].total || 0} занято`}
                      note={`${roomsByBuilding["2"].busyPercent}% загрузка - нажмите для корпуса 1`}
                      icon={<RoomsIcon />}
                      accent="from-teal-500 to-cyan-500"
                      glyph="⌂"
                      glyphColor="#0f766e"
                    />
                  </div>
                </div>
              </button>
            </div>
            <StatCard
              title="Актуальность данных"
              value={fmtDateTime(metrics.lastReparseSuccess)}
              note={`Изменения: +${metrics.changesAddedCount} / -${metrics.changesRemovedCount}`}
              icon={<SyncIcon />}
              accent="from-amber-500 to-orange-500"
              glyph="⟳"
              glyphColor="#f97316"
              delay={200}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-2">
            <div
              className={`rounded-2xl p-4 sm:p-6 border border-slate-200/80 bg-white/92 backdrop-blur-sm shadow-[0_10px_30px_rgba(15,23,42,0.08)] transition-all duration-500 ${
                uiReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
              }`}
              style={{ transitionDelay: "260ms" }}
            >
              <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-1">Нагрузка по дням недели</h3>
              <div className="mb-3 sm:mb-4 text-xs text-slate-500">{weekRangeLabel}</div>
              <div className="h-64 sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weekLoadData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" />
                    <XAxis dataKey="day" stroke="#64748b" tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} stroke="#64748b" tickLine={false} axisLine={false} />
                    <Tooltip
                      formatter={(value) => [`${value}`, "Занятия"]}
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #cbd5e1",
                        background: "#ffffff",
                        boxShadow: "0 8px 24px rgba(15,23,42,0.10)",
                      }}
                    />
                    <Bar dataKey="lessons" name="Занятия" fill="#64748b" radius={[10, 10, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div
              className={`rounded-2xl p-4 sm:p-6 border border-slate-200/80 bg-white/92 backdrop-blur-sm shadow-[0_10px_30px_rgba(15,23,42,0.08)] transition-all duration-500 ${
                uiReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
              }`}
              style={{ transitionDelay: "320ms" }}
            >
              <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-3 sm:mb-4">Ближайшие занятия</h2>
              {recent.length > 0 ? (
                <ul className="space-y-2">
                  {recent.map((r, i) => (
                    <li key={i} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm text-slate-700 transition-colors hover:bg-slate-100">
                      {r}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm text-slate-600">
                  Нет ближайших событий
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
